import { Link } from "react-router-dom";
import { patientHref } from "@/lib/patient-links";
import { useViewPath } from "./SupportView";

// How every queue row names its patient. One component so the work queue, the
// review queue, the chat-draft queue and the meeting list read identically —
// they each used to invent their own (a truncated UUID, six bare digits, or
// nothing), which made the same person unrecognisable between two screens.
//
// The ladder matches the inbox/patient-list convention that already existed:
// name first, personnummer shorthand as the disambiguator, "—" for a row with
// no captured name (pre-name-capture, dev-auth, anonymised). Only when there is
// no identifier at all — an older API response that carried neither field, or a
// mock row with no personnummer — do we fall back to the id fragment, so the
// clinician always has *some* stable handle instead of a blank cell.
//
// TES-955 (TES-941) — "Patientens namn ska alltid vara klickbart". `link`
// makes the name a link to the patient's overview. Opt-in, because several
// rows are themselves a link or a button, and a link inside a link is invalid
// HTML that browsers resolve unpredictably.
export function PatientLabel({
  patientId,
  name,
  personalNumberLast6,
  className = "",
  link = false,
}: {
  patientId: string;
  name?: string | null;
  personalNumberLast6?: string;
  className?: string;
  link?: boolean;
}) {
  const viewPath = useViewPath();
  const last6 = personalNumberLast6?.trim() ?? "";
  const displayName = name?.trim() ?? "";

  if (!displayName && !last6) {
    return (
      <span className={`font-mono text-xs text-textSecondary ${className}`}>
        patient {patientId.slice(0, 8)}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-baseline gap-xs ${className}`}>
      {link ? (
        <Link
          to={viewPath(patientHref(patientId))}
          className="text-textPrimary hover:text-brand hover:underline"
        >
          {displayName || "—"}
        </Link>
      ) : (
        <span className="text-textPrimary">{displayName || "—"}</span>
      )}
      {last6 ? (
        <span className="font-mono text-xs text-textSecondary">…{last6}</span>
      ) : null}
    </span>
  );
}
